/**
 * FinancesPage Component
 *
 * Admin page for viewing financial statistics and payment history.
 * Similar to Budgira's finances page with:
 * - Summary cards (revenue, completed payments, pending, refunds)
 * - Paginated payment history table
 */

import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../store';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Payment record from the API
 */
interface PaymentRecord {
  id: string;
  date: string;
  userId: string;
  userEmail: string;
  paymentType: 'subscription' | 'one-time' | null;
  amount: number;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  endedReason: string | null;
  paypalReference: string | null;
}

/**
 * Financial summary from the API
 */
interface FinancialSummary {
  revenue: number;
  completedPayments: number;
  pendingPayments: number;
  refunds: number;
}

/**
 * API response for finances endpoint
 */
interface FinancesResponse {
  success: boolean;
  data: {
    summary: FinancialSummary;
    payments: PaymentRecord[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * Format date for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get payment type label
 */
function getPaymentTypeLabel(paymentType: string | null): string {
  switch (paymentType) {
    case 'subscription':
      return 'Abonnement annuel';
    case 'one-time':
      return 'Paiement unique';
    default:
      return 'Inconnu';
  }
}

/**
 * Get status badge style and label
 */
function getStatusBadge(status: string, endedReason: string | null): { label: string; className: string } {
  if (endedReason === 'cancelled_by_admin') {
    return { label: 'Remboursé', className: 'bg-orange-100 text-orange-700' };
  }
  
  switch (status) {
    case 'completed':
    case 'active':
      return { label: 'Complété', className: 'bg-green-100 text-green-700' };
    case 'cancelled':
      return { label: 'Annulé', className: 'bg-red-100 text-red-700' };
    case 'expired':
      return { label: 'Expiré', className: 'bg-slate-100 text-slate-700' };
    default:
      return { label: status, className: 'bg-slate-100 text-slate-700' };
  }
}

export function FinancesPage() {
  const token = useStore((state) => state.token);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 20;

  /**
   * Fetch finances data from API
   */
  const fetchFinances = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/finances?page=${page}&limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result: FinancesResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error('Erreur lors du chargement des données financières');
      }

      setSummary(result.data.summary);
      setPayments(result.data.payments);
      setTotal(result.pagination.total);
      setHasMore(result.pagination.hasMore);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchFinances();
  }, [fetchFinances]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-slate-900">Finances</h1>
        <p className="text-slate-500 text-sm mt-1">Suivi des revenus et historique des paiements</p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Revenue */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Chiffre d'affaires</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-primary-600">
              {formatCurrency(summary?.revenue || 0)}
            </p>
          )}
        </div>

        {/* Completed payments */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Paiements réussis</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-slate-900">
              {summary?.completedPayments || 0}
            </p>
          )}
        </div>

        {/* Pending payments */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">En attente</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-amber-600">
              {summary?.pendingPayments || 0}
            </p>
          )}
        </div>

        {/* Refunds */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Remboursés</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-red-600">
              {summary?.refunds || 0}
            </p>
          )}
        </div>
      </div>

      {/* Payment history table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">Historique des paiements</h2>
          <p className="text-sm text-slate-500">{total} paiement(s) au total</p>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
            <p className="text-slate-500 mt-2">Chargement...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Aucun paiement enregistré
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Référence PayPal
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((payment) => {
                    const statusBadge = getStatusBadge(payment.status, payment.endedReason);
                    return (
                      <tr key={payment.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {formatDate(payment.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {payment.userEmail}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {getPaymentTypeLabel(payment.paymentType)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right font-medium">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-mono text-xs">
                          {payment.paypalReference || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {payments.map((payment) => {
                const statusBadge = getStatusBadge(payment.status, payment.endedReason);
                return (
                  <div key={payment.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{formatDate(payment.date)}</span>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-slate-900">{payment.userEmail}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{getPaymentTypeLabel(payment.paymentType)}</span>
                      <span className="text-sm font-bold text-slate-900">{formatCurrency(payment.amount)}</span>
                    </div>
                    {payment.paypalReference && (
                      <div className="text-xs text-slate-400 font-mono truncate">
                        Réf: {payment.paypalReference}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Page {page} sur {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FinancesPage;
